// Inside buildings, in the Inkwell style: the room (floor, walls, furniture) is painted once into a canvas at
// three pixels per game pixel (one per ink unit); windows show the sky through the day, and sconces, ovens and
// fireplaces are lights at night.
import ink from '../ink/ink.js';
import type { Interior, Furniture } from '../../../shared/interiors.js';
import type { Light } from './sky.js';

const I = ink as any;
const { INK, TAU, blob, oval, line, poly, rrect, shade, mulberry, star4, cGlow } = I;
const TS = 16, K = 3, T = 48;

export interface Room { def: Interior; bg: HTMLCanvasElement; lights: Light[]; windows: { x: number; y: number; w: number; h: number }[] }

export function buildRoom(def: Interior): Room {
  const W = def.w * T, H = def.h * T, bg = document.createElement('canvas'); bg.width = W; bg.height = H;
  const g = bg.getContext('2d')!, r = mulberry(def.w * 31 + def.h * 7 + def.exit[0]);
  g.lineJoin = 'round'; g.lineCap = 'round';
  // the floor
  const fx0 = T, fy0 = 2 * T, fw = W - 2 * T, fh = H - 2 * T;
  if (def.floor === 'wood') {
    g.fillStyle = '#d9b48a'; g.fillRect(fx0, fy0, fw, fh);
    for (let y = fy0; y < H; y += 16) {
      g.fillStyle = (y / 16) % 2 ? '#d3ab7f' : '#ddb98f'; g.fillRect(fx0, y, fw, 16);
      line(g, [[fx0, y], [fx0 + fw, y]], 10 + y, 1.4, 'rgba(62,47,40,0.35)');
      for (let x = fx0 + ((y * 7) % 90); x < fx0 + fw; x += 70 + r() * 60) line(g, [[x, y + 1], [x, y + 15]], 20 + x + y, 1.2, 'rgba(62,47,40,0.3)');
      if (r() < 0.5) { const kx = fx0 + r() * fw; oval(g, kx, y + 8, 3, 1.6, null, 30 + y, 0.8, 8); }
    }
  } else if (def.floor === 'stone') {
    g.fillStyle = '#d6cfc2'; g.fillRect(fx0, fy0, fw, fh);
    for (let y = fy0; y < H; y += 24) for (let x = fx0 + ((y / 24) % 2) * 18; x < fx0 + fw; x += 36) {
      g.fillStyle = ['#d0c8ba', '#dad3c6', '#ccc4b5'][Math.floor(r() * 3)]; rrect(g, x + 1.5, y + 1.5, 33, 21, 5); g.fill(); g.lineWidth = 1.2; g.strokeStyle = 'rgba(62,47,40,0.3)'; g.stroke();
    }
  } else {
    for (let y = fy0; y < H; y += 24) for (let x = fx0; x < fx0 + fw; x += 24) { g.fillStyle = ((x + y) / 24) % 2 ? '#f3ead6' : '#e9dcc0'; g.fillRect(x, y, 24, 24); }
    for (let y = fy0; y < H; y += 24) line(g, [[fx0, y], [fx0 + fw, y]], 40 + y, 0.8, 'rgba(62,47,40,0.18)');
  }
  // soft light pooling in the middle of the room
  const gl = g.createRadialGradient(W / 2, H * 0.55, 10, W / 2, H * 0.55, Math.max(W, H) * 0.6); gl.addColorStop(0, 'rgba(255,248,226,0.28)'); gl.addColorStop(1, 'rgba(62,47,40,0.12)');
  g.fillStyle = gl; g.fillRect(fx0, fy0, fw, fh);
  // the back wall: plaster, a wainscot rail and the line where it meets the floor
  const wall = def.wall, wd = shade(wall, -0.12);
  g.fillStyle = wall; g.fillRect(0, 0, W, 2 * T);
  for (let x = 12; x < W; x += 24) line(g, [[x, 6], [x, 2 * T - 26]], 60 + x, 1, 'rgba(62,47,40,0.08)');
  g.fillStyle = def.trim; g.fillRect(0, 2 * T - 22, W, 18); line(g, [[0, 2 * T - 22], [W, 2 * T - 22]], 70, 1.6, 'rgba(62,47,40,0.6)');
  g.fillStyle = shade(def.trim, -0.15); g.fillRect(0, 2 * T - 4, W, 4);
  line(g, [[0, 2 * T], [W, 2 * T]], 71, 2.4, INK);
  // side walls (seen from above) and the front wall with its doorway
  for (const sx of [0, W - T]) { g.fillStyle = shade(wall, -0.2); g.fillRect(sx, 0, T, H); g.fillStyle = 'rgba(255,255,255,0.12)'; g.fillRect(sx + (sx ? 4 : T - 10), 0, 6, H); }
  line(g, [[T, 2 * T], [T, H]], 72, 2.4, INK); line(g, [[W - T, 2 * T], [W - T, H]], 73, 2.4, INK);
  const ex = def.exit[0] * T;
  g.fillStyle = shade(wall, -0.32); g.fillRect(0, H - 16, W, 16); line(g, [[0, H - 16], [W, H - 16]], 74, 2, INK);
  g.fillStyle = '#9b7653'; rrect(g, ex - T, H - 18, 2 * T, 18, 4); g.fill(); g.lineWidth = 2; g.strokeStyle = INK; g.stroke();
  g.fillStyle = '#c9705e'; rrect(g, ex - T + 8, H - 40, 2 * T - 16, 18, 6); g.fill(); g.lineWidth = 1.6; g.stroke();   // a doormat
  line(g, [[0, 1], [W, 1]], 75, 2.4, INK);
  const lights: Light[] = [], windows: Room['windows'] = [];
  for (const it of def.furniture.filter(i => i.kind === 'rug')) furniture(g, it, lights, windows, wd);
  for (const it of def.furniture.filter(i => i.kind !== 'rug').sort((a, b) => (a.y + a.h) - (b.y + b.h))) furniture(g, it, lights, windows, wd);
  return { def, bg, lights, windows };
}

const shadow = (g: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry = rx * 0.25) => { g.fillStyle = 'rgba(62,47,40,0.16)'; g.beginPath(); g.ellipse(cx, cy, rx, ry, 0, 0, TAU); g.fill(); };

function furniture(g: CanvasRenderingContext2D, it: Furniture, lights: Light[], windows: Room['windows'], wallDark: string): void {
  const x = it.x * T, y = it.y * T, w = it.w * T, h = it.h * T, id = it.x * 97 + it.y * 13, cx = x + w / 2;
  const box = (x0: number, y0: number, ww: number, hh: number, col: string, k = 0, rad = 6) => { g.fillStyle = col; rrect(g, x0, y0, ww, hh, rad); g.fill(); g.lineWidth = 2; g.strokeStyle = INK; g.stroke(); void k; };
  switch (it.kind) {
    case 'window': {
      box(x + 6, y + 9, w - 12, 62, '#a6d4e4', 0, 8);
      windows.push({ x: (x + 9) / K, y: (y + 12) / K, w: (w - 18) / K, h: 56 / K });
      box(x + 2, y + 70, w - 4, 9, '#c79a6a', 0, 3);
      [[x + 14, y + 74], [x + w - 14, y + 74]].forEach(([px, py], i) => { oval(g, px, py - 6, 6, 5, i ? '#f6c1cf' : '#f7dd7a', id + i, 1.2, 8); });
      break;
    }
    case 'backdoor': {   // the back door, out to your garden: a green door in the back wall with a little window
      const dw = Math.min(w - 20, 64), dx = cx - dw / 2, top = y + 2, bot = 2 * T;
      g.fillStyle = 'rgba(62,47,40,0.18)'; rrect(g, dx - 5, top - 3, dw + 10, bot - top + 3, 12); g.fill();
      box(dx, top, dw, bot - top, '#6f9e56', 0, 10);
      for (const lx of [dx + dw / 3, dx + dw * 2 / 3]) line(g, [[lx, top + 30], [lx, bot - 6]], id + lx, 1.2, 'rgba(62,47,40,0.35)');
      box(cx - 12, top + 8, 24, 16, '#bfe0ea', 0, 6);
      oval(g, dx + dw - 10, top + (bot - top) * 0.6, 3.5, 3.5, '#f2c14e', id + 3, 1.2, 8);
      break;
    }
    case 'sconce': {
      line(g, [[cx, y + 30], [cx, y + 44]], id, 3, '#8d98a3');
      blob(g, [[cx - 8, y + 18], [cx + 8, y + 18], [cx + 6, y + 32], [cx - 6, y + 32]], '#e8d2b0', id + 1, 1.8, 0.3);
      blob(g, [[cx, y + 8], [cx + 4, y + 16], [cx, y + 20], [cx - 4, y + 16]], '#f7d35e', id + 2, 1.4, 0.3);
      lights.push({ x: (cx) / K, y: (y + 30) / K, r: 58, warm: true }); break;
    }
    case 'banner': {
      line(g, [[cx - 18, y + 6], [cx + 18, y + 6]], id, 3, '#9b7653');
      poly(g, [[cx - 14, y + 6], [cx + 14, y + 6], [cx + 14, y + 62], [cx, y + 52], [cx - 14, y + 62]], it.color ?? '#7f97c2', id + 1, 2);
      star4(g, cx, y + 28, 7, 0, '#f2c14e', 1.4); break;
    }
    case 'oven': {
      box(x, y - 18, w, h + 18, '#c9705e', 0, 10);
      for (let i = 0; i < 4; i++) line(g, [[x + 4, y - 4 + i * 12], [x + w - 4, y - 4 + i * 12]], id + i, 1.2, 'rgba(62,47,40,0.3)');
      g.fillStyle = INK; rrect(g, x + 14, y + h - 46, w - 28, 36, 14); g.fill();
      g.fillStyle = '#f28b4b'; rrect(g, x + 20, y + h - 36, w - 40, 22, 10); g.fill(); g.fillStyle = '#f7d35e'; rrect(g, x + 28, y + h - 30, w - 56, 10, 5); g.fill();
      lights.push({ x: cx / K, y: (y + h - 24) / K, r: 44, warm: true, fire: true }); break;
    }
    case 'fireplace': {
      box(x - 6, y - 28, w + 12, h + 28, '#c9c1b4', 0, 8);
      for (let i = 0; i < 3; i++) line(g, [[x, y - 12 + i * 16], [x + w, y - 12 + i * 16]], id + i, 1.2, 'rgba(62,47,40,0.3)');
      box(x - 10, y - 34, w + 20, 10, '#9b7653', 0, 3);
      g.fillStyle = '#3a2e2a'; rrect(g, x + 12, y + 2, w - 24, h - 8, 14); g.fill();
      blob(g, [[cx - 18, y + h - 10], [cx - 8, y + 14], [cx, y + h - 22], [cx + 10, y + 10], [cx + 18, y + h - 10]], '#f28b4b', id + 9, 1.4, 0.5);
      blob(g, [[cx - 9, y + h - 10], [cx, y + 22], [cx + 9, y + h - 10]], '#f7d35e', id + 10, 1, 0.4);
      lights.push({ x: cx / K, y: (y + h - 18) / K, r: 74, warm: true, fire: true }); break;
    }
    case 'counter': {
      shadow(g, cx, y + h + 2, w * 0.5);
      box(x, y + 6, w, h - 6, '#c79a6a', 0, 8);
      g.fillStyle = '#e8c79c'; rrect(g, x + 3, y + 8, w - 6, 10, 5); g.fill();
      for (let i = T / 2; i < w; i += T) line(g, [[x + i, y + 22], [x + i, y + h - 4]], id + i, 1.2, 'rgba(62,47,40,0.35)');
      break;
    }
    case 'shelf_bread': case 'shelf_books': {
      box(x, y - 30, w, h + 30, '#9b7653', 0, 6);
      for (const sy of [y - 8, y + 22]) line(g, [[x + 4, sy], [x + w - 4, sy]], id + sy, 2, INK);
      for (let i = 8; i < w - 8; i += 14) {
        if (it.kind === 'shelf_bread') { oval(g, x + i + 5, y - 15, 6, 5, '#e2a95c', id + i, 1.4, 9); oval(g, x + i + 5, y + 15, 6, 5, '#c98a4b', id + i + 1, 1.4, 9); }
        else { const c = ['#e3604b', '#7f97c2', '#86b867', '#f2c14e', '#9a7cc2'][(i / 14) % 5 | 0]; poly(g, [[x + i, y - 26], [x + i + 9, y - 26], [x + i + 9, y - 9], [x + i, y - 9]], c, id + i, 1.4); poly(g, [[x + i + 2, y + 4], [x + i + 11, y + 4], [x + i + 11, y + 21], [x + i + 2, y + 21]], ['#9a7cc2', '#e3604b', '#7f97c2'][(i / 14) % 3 | 0], id + i + 1, 1.4); }
      }
      break;
    }
    case 'table': {
      shadow(g, cx, y + h - 2, w * 0.45);
      [[x + 8, y + h - 8], [x + w - 8, y + h - 8]].forEach(([lx, ly], i) => line(g, [[lx, ly - 14], [lx, ly]], id + i, 3, INK));
      box(x + 3, y + 8, w - 6, h - 20, '#c79a6a', 0, 10);
      g.fillStyle = 'rgba(255,255,255,0.22)'; rrect(g, x + 8, y + 11, w - 16, 6, 3); g.fill();
      if (w >= 2 * T) { oval(g, cx, y + 18, 9, 5, '#fbf7ee', id + 5, 1.4, 10); oval(g, cx, y + 16, 5, 3, '#e3604b', id + 6, 1, 8); }
      else oval(g, cx, y + 17, 7, 5, '#e2a95c', id + 5, 1.4, 9);
      break;
    }
    case 'forge': {   // a stone hearth with coals and a hood
      box(x - 4, y - 30, w + 8, 26, '#8a8f99', 0, 6);
      box(x, y - 8, w, h + 8, '#a7a29a', 0, 8);
      for (let i = 0; i < 3; i++) line(g, [[x + 2, y + 6 + i * 16], [x + w - 2, y + 6 + i * 16]], id + i, 1.2, 'rgba(62,47,40,0.3)');
      g.fillStyle = '#3a2e2a'; rrect(g, x + 14, y + 10, w - 28, h - 30, 12); g.fill();
      blob(g, [[cx - 26, y + h - 22], [cx - 10, y + 22], [cx, y + h - 34], [cx + 12, y + 20], [cx + 26, y + h - 22]], '#f28b4b', id + 9, 1.4, 0.5);
      blob(g, [[cx - 12, y + h - 22], [cx, y + 30], [cx + 12, y + h - 22]], '#f7d35e', id + 10, 1, 0.4);
      lights.push({ x: cx / K, y: (y + h - 30) / K, r: 80, warm: true, fire: true }); break;
    }
    case 'anvil': shadow(g, cx, y + h - 4, 20);
      box(cx - 8, y + 24, 16, 18, '#5d6a7a', 0, 3);
      poly(g, [[x + 4, y + 14], [x + w - 4, y + 14], [x + w - 10, y + 26], [x + 10, y + 26]], '#7f8ea0', id, 1.8);
      line(g, [[x + 8, y + 16], [x + w - 12, y + 16]], id + 1, 1.2, 'rgba(255,255,255,0.4)'); break;
    case 'workbench': {
      shadow(g, cx, y + h - 2, w * 0.45);
      [[x + 8], [x + w - 8]].forEach(([lx], i) => line(g, [[lx, y + 28], [lx, y + h - 4]], id + i, 3.2, INK));
      box(x + 2, y + 10, w - 4, 18, '#b98a5a', 0, 4);
      line(g, [[x + 14, y + 14], [x + 34, y + 20]], id + 3, 2.4, '#8a8f99');                 // a saw
      oval(g, x + w - 22, y + 16, 6, 4, '#9b7653', id + 4, 1.4, 8);                          // a mallet
      line(g, [[x + w - 22, y + 18], [x + w - 34, y + 24]], id + 5, 2, '#7a5a3a'); break;
    }
    case 'loom': {
      shadow(g, cx, y + h - 2, w * 0.45);
      for (const lx of [x + 6, x + w - 6]) line(g, [[lx, y - 10], [lx, y + h - 4]], id + lx, 3.4, INK);
      box(x + 2, y - 14, w - 4, 10, '#9b7653', 0, 3); box(x + 2, y + h - 30, w - 4, 10, '#9b7653', 0, 3);
      const cols = ['#b48ab7', '#f29bb8', '#7f97c2', '#f2c14e'];
      for (let i = 0; i < 4; i++) { g.fillStyle = cols[i]; g.fillRect(x + 12, y - 2 + i * 14, w - 24, 12); }
      for (let i = x + 14; i < x + w - 12; i += 6) line(g, [[i, y - 4], [i, y + h - 32]], id + i, 0.8, 'rgba(62,47,40,0.35)');
      break;
    }
    case 'yarn': for (let i = 0; i < it.w * 2; i++) { const yx = x + 12 + i * 22, yy = y + 22 + (i % 2) * 8; shadow(g, yx, yy + 10, 10); oval(g, yx, yy, 10, 9, ['#b48ab7', '#f29bb8', '#7f97c2', '#f2c14e'][i % 4], id + i, 1.6, 9); line(g, [[yx - 6, yy - 3], [yx + 6, yy + 3]], id + i + 9, 1, 'rgba(62,47,40,0.35)'); } break;
    case 'fishtub': {
      shadow(g, cx, y + h - 2, w * 0.45);
      box(x + 4, y + 6, w - 8, h - 10, '#9b7653', 0, 10);
      g.fillStyle = '#8fd3e8'; rrect(g, x + 10, y + 10, w - 20, h - 22, 8); g.fill();
      for (let i = 0; i < 3; i++) { const fx = x + 20 + i * (w - 40) / 2; poly(g, [[fx - 8, y + 20], [fx + 6, y + 16], [fx + 6, y + 24]], ['#e3604b', '#f2c14e', '#7f97c2'][i], id + i, 1.2); }
      break;
    }
    case 'bench': shadow(g, cx, y + h - 6, w * 0.45); box(x + 3, y + 18, w - 6, 14, '#c79a6a', 0, 5); [[x + 8], [x + w - 8]].forEach(([lx], i) => line(g, [[lx, y + 32], [lx, y + 40]], id + i, 3, INK)); break;
    case 'stool': shadow(g, cx, y + h - 6, 12); line(g, [[cx - 7, y + 30], [cx - 8, y + 42]], id, 2.6, INK); line(g, [[cx + 7, y + 30], [cx + 8, y + 42]], id + 1, 2.6, INK); oval(g, cx, y + 26, 14, 7, '#c79a6a', id + 2, 2, 10); break;
    case 'plant': {
      shadow(g, cx, y + h - 2, 14);
      poly(g, [[cx - 12, y + 28], [cx + 12, y + 28], [cx + 9, y + h - 2], [cx - 9, y + h - 2]], '#c9705e', id, 2);
      for (let i = 0; i < 5; i++) { const a = -Math.PI / 2 + (i - 2) * 0.55; I.cLeaf(g, cx, y + 28, 24, a, i % 2 ? '#86b867' : '#9cc46a', id + i * 3, 1.6); }
      break;
    }
    case 'flowers': {
      box(x, y + 10, w, h - 10, '#9b7653', 0, 6);
      for (let i = 10; i < w - 6; i += 14) for (let j = 0; j < h - 20; j += 18) { line(g, [[x + i, y + 6 + j + 10], [x + i, y + 6 + j + 22]], id + i + j, 1.4, '#6d9e56'); oval(g, x + i, y + 6 + j + 8, 5, 5, ['#f6c1cf', '#f7dd7a', '#ffffff', '#c9b8f0', '#e3604b'][(i + j) % 5], id + i * 3 + j, 1.2, 8); }
      break;
    }
    case 'crate': shadow(g, cx, y + h - 2, 20); box(x + 4, y + 6, w - 8, h - 8, '#c79a6a', 0, 4); line(g, [[x + 6, y + 8], [x + w - 6, y + h - 4]], id, 1.4, 'rgba(62,47,40,0.45)'); line(g, [[x + w - 6, y + 8], [x + 6, y + h - 4]], id + 1, 1.4, 'rgba(62,47,40,0.45)'); break;
    case 'barrel': shadow(g, cx, y + h - 2, 18); box(x + 8, y + 4, w - 16, h - 6, '#b98a5a', 0, 12); [y + 14, y + h - 14].forEach((hy, i) => line(g, [[x + 9, hy], [x + w - 9, hy]], id + i, 2, '#5d6a7a')); break;
    case 'chest': shadow(g, cx, y + h - 2, 20); box(x + 4, y + 14, w - 8, h - 16, '#b98a5a', 0, 6); box(x + 3, y + 8, w - 6, 14, '#c79a6a', 0, 7); oval(g, cx, y + 24, 4, 5, '#f2c14e', id, 1.4, 8); break;
    case 'board': {
      box(x, y - 22, w, h + 12, '#c79a6a', 0, 6);
      [[6, -16], [26, -14], [46, -18], [14, 6], [38, 6]].forEach(([a, b], i) => { if (a + 18 > w) return; poly(g, [[x + a, y + b], [x + a + 16, y + b], [x + a + 16, y + b + 18], [x + a, y + b + 18]], i % 2 ? '#fdf3d6' : '#fbf7ee', id + i, 1.2); oval(g, x + a + 8, y + b + 2, 2, 2, '#e3604b', id + i + 10, 0.8, 6); });
      break;
    }
    case 'bed': {
      shadow(g, cx, y + h, w * 0.5);
      box(x, y - 10, w, h + 10, '#9b7653', 0, 10);
      g.fillStyle = '#fbf7ee'; rrect(g, x + 6, y - 4, w - 12, 22, 8); g.fill(); g.lineWidth = 1.6; g.strokeStyle = INK; g.stroke();   // the pillow
      const col = it.color ?? '#7f97c2';
      g.fillStyle = col; rrect(g, x + 4, y + 18, w - 8, h - 22, 10); g.fill(); g.lineWidth = 2; g.stroke();
      g.fillStyle = shade(col, 0.25); rrect(g, x + 4, y + 18, w - 8, 9, 6); g.fill();
      line(g, [[x + 10, y + h * 0.65], [x + w - 10, y + h * 0.6]], id, 1.2, 'rgba(62,47,40,0.3)');
      break;
    }
    case 'rug': {
      const c = it.color ?? '#e3604b';
      g.fillStyle = c; rrect(g, x + 4, y + 6, w - 8, h - 12, 14); g.fill(); g.lineWidth = 2; g.strokeStyle = INK; g.stroke();
      g.lineWidth = 2; g.strokeStyle = shade(c, 0.35); rrect(g, x + 12, y + 14, w - 24, h - 28, 10); g.stroke();
      break;
    }
  }
  void wallDark; void cGlow;
}

/** Window panes painted with today's sky: blue by day, warm at dusk, dark with a star at night, grey in rain. */
export function drawWindows(ctx: CanvasRenderingContext2D, room: Room, hour: number, rain: number, t: number): void {
  const night = hour >= 20.5 || hour < 5, dusk = hour >= 18.5 && hour < 20.5, dawn = hour >= 5 && hour < 7;
  let top = night ? '#2e3a6a' : dusk ? '#f2a46b' : dawn ? '#f6c3b0' : '#a6d4e4', bottom = night ? '#46528a' : dusk ? '#d98a8a' : dawn ? '#f9dfa8' : '#d8eef4';
  if (rain > 0.3) { top = night ? '#2f3650' : '#9aa6b4'; bottom = night ? '#3a4460' : '#bcc5cf'; }
  for (const w of room.windows) {
    const gr = ctx.createLinearGradient(0, w.y, 0, w.y + w.h); gr.addColorStop(0, top); gr.addColorStop(1, bottom);
    ctx.save(); ctx.beginPath(); rrect(ctx, w.x, w.y, w.w, w.h, 2); ctx.clip();
    ctx.fillStyle = gr; ctx.fillRect(w.x, w.y, w.w, w.h);
    if (!night && rain < 0.3) { ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.beginPath(); ctx.ellipse(w.x + w.w * 0.3 + Math.sin(t * 0.05) * 2, w.y + w.h * 0.35, 3, 1.4, 0, 0, TAU); ctx.fill(); }
    if (night && rain < 0.3) { ctx.fillStyle = '#fff3c4'; ctx.fillRect(w.x + 3, w.y + 3, 0.8, 0.8); ctx.fillRect(w.x + w.w - 5, w.y + 6, 0.8, 0.8); }
    if (rain > 0.3) { ctx.strokeStyle = 'rgba(230,238,255,.7)'; ctx.lineWidth = 0.4; for (let i = 0; i < 5; i++) { const rx = w.x + ((i * 7 + t * 30) % w.w), ry = w.y + ((i * 5 + t * 60) % w.h); ctx.beginPath(); ctx.moveTo(rx, ry); ctx.lineTo(rx - 0.5, ry + 2); ctx.stroke(); } }
    ctx.restore();
    ctx.strokeStyle = INK; ctx.lineWidth = 0.8; ctx.beginPath(); ctx.moveTo(w.x + w.w / 2, w.y); ctx.lineTo(w.x + w.w / 2, w.y + w.h); ctx.moveTo(w.x, w.y + w.h / 2); ctx.lineTo(w.x + w.w, w.y + w.h / 2); ctx.stroke();
  }
}

/** A small floor plan of a room for the minimap: floor, walls, furniture as soft blocks, the way out. */
export function roomThumb(room: Room): HTMLCanvasElement {
  const d = room.def, cv = document.createElement('canvas'); cv.width = d.w * 4; cv.height = d.h * 4;
  const g = cv.getContext('2d')!;
  g.drawImage(room.bg, 0, 0, cv.width, cv.height);
  return cv;
}
